/**
 * ImageGroup — Grid agrupado de imagens (estilo WhatsApp) com lightbox.
 *
 * Layouts: 1 = full, 2 = 2-col, 3 = 1+2, 4+ = 2x2 com "+N".
 * Clique abre lightbox navegável em vez de nova aba.
 *
 * Princípios: SRP — layout de grid de imagens + delegação para lightbox.
 */
import React, { useEffect, useState } from 'react';
import { ChatMessage } from '../../hooks/types';
import { MediaLightbox, LightboxItem } from './media/MediaLightbox';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { cn } from '@/lib/utils';
import { isTemporaryRemoteMediaUrl, markImageAsFailed } from '../../../../utils/failedImageCache';

interface ImageGroupProps {
    messages: ChatMessage[];
    isMe: boolean;
}

/** Miniatura com fallback só em onError real (lazy load ≠ falha). */
function GroupThumb({
    url,
    className,
    onThumbClick,
}: {
    url?: string;
    className?: string;
    onThumbClick?: () => void;
}) {
    const [failed, setFailed] = useState(() => isTemporaryRemoteMediaUrl(url));

    useEffect(() => {
        setFailed(isTemporaryRemoteMediaUrl(url));
    }, [url]);

    if (!url || failed) {
        return (
            <div
                className={cn(
                    'flex items-center justify-center bg-muted/40 text-muted-foreground',
                    className
                )}
                aria-hidden
            >
                <AppIcon name="image" className="h-8 w-8 opacity-60" />
            </div>
        );
    }

    return (
        <img
            src={url}
            alt=""
            className={className}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onClick={onThumbClick}
            onError={() => {
                markImageAsFailed(url);
                setFailed(true);
            }}
        />
    );
}

export const ImageGroup: React.FC<ImageGroupProps> = ({ messages, isMe: _isMe }) => {
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

    if (messages.length === 0) return null;

    const lightboxItems: LightboxItem[] = messages
        .filter((m) => m.media?.url)
        .map((m) => ({
            url: m.media!.url,
            type: (m.type === 'video' ? 'video' : 'image') as 'image' | 'video',
            caption: m.text && m.text.toLowerCase() !== 'image' ? m.text : undefined,
        }));

    const openLightbox = (index: number) => setLightboxIndex(index);

    // Single image → full width
    if (messages.length === 1) {
        return (
            <>
                <div className="max-w-xs cursor-pointer" onClick={() => openLightbox(0)}>
                    <GroupThumb
                        url={messages[0].media?.url}
                        className="w-full h-auto max-h-80 rounded-lg object-cover"
                    />
                </div>
                {lightboxIndex !== null && (
                    <MediaLightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
                )}
            </>
        );
    }

    // 2 images → side by side
    if (messages.length === 2) {
        return (
            <>
                <div className="grid max-w-xs grid-cols-2 gap-0.5 overflow-hidden rounded-lg">
                    {messages.map((msg, i) => (
                        <GroupThumb
                            key={msg.id}
                            url={msg.media?.url}
                            className="h-32 w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                            onThumbClick={() => openLightbox(i)}
                        />
                    ))}
                </div>
                {lightboxIndex !== null && (
                    <MediaLightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
                )}
            </>
        );
    }

    // 3 images → 1 big + 2 small
    if (messages.length === 3) {
        return (
            <>
                <div className="max-w-xs overflow-hidden rounded-lg">
                    <GroupThumb
                        url={messages[0].media?.url}
                        className="h-40 w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                        onThumbClick={() => openLightbox(0)}
                    />
                    <div className="mt-0.5 grid grid-cols-2 gap-0.5">
                        {messages.slice(1).map((msg, i) => (
                            <GroupThumb
                                key={msg.id}
                                url={msg.media?.url}
                                className="h-24 w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                                onThumbClick={() => openLightbox(i + 1)}
                            />
                        ))}
                    </div>
                </div>
                {lightboxIndex !== null && (
                    <MediaLightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
                )}
            </>
        );
    }

    // 4+ images → 2x2 grid with "+N" overlay on last
    const shown = messages.slice(0, 4);
    const remaining = messages.length - 4;

    return (
        <>
            <div className="max-w-xs overflow-hidden rounded-lg">
                <div className="grid grid-cols-2 gap-0.5">
                    {shown.map((msg, i) => (
                        <div key={msg.id} className="relative cursor-pointer" onClick={() => openLightbox(i)}>
                            <GroupThumb
                                url={msg.media?.url}
                                className="h-28 w-full object-cover transition-opacity hover:opacity-90"
                            />
                            {i === 3 && remaining > 0 && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                                    <span className="text-2xl font-bold text-white">+{remaining}</span>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            {lightboxIndex !== null && (
                <MediaLightbox items={lightboxItems} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
            )}
        </>
    );
};
